import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { adminApi } from '../../lib/adminAuth';
import { Loader2, User, Phone, Mail, MapPin, Calendar, Home as HomeIcon, Building2, Tag, Eye, EyeOff, ExternalLink, Camera } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { useToast } from '../../hooks/use-toast';

const STATUS_META = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700' },
  closed: { label: 'Closed', color: 'bg-gray-100 text-gray-600' },
};

const INTENT_META = {
  sell: { label: 'Sell', color: 'bg-[#fef2f4] text-[#9a1b2f]' },
  rent: { label: 'Rent Out', color: 'bg-emerald-50 text-emerald-700' },
};

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const AdminPropertySubmissionsPage = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { toast } = useToast();

  const load = () => {
    setLoading(true);
    const params = { limit: 200 };
    if (filter !== 'all') params.intent = filter;
    adminApi.get('/property-submissions', { params })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filter]);

  const changeStatus = async (id, status) => {
    try {
      await adminApi.patch(`/property-submissions/${id}`, null, { params: { status } });
      setItems((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      toast({ title: 'Status updated', description: STATUS_META[status].label });
    } catch (err) {
      toast({ title: 'Failed to update', description: err.response?.data?.detail || 'Try again' });
    }
  };

  const togglePublish = async (id, published) => {
    try {
      await adminApi.patch(`/property-submissions/${id}`, null, { params: { published } });
      setItems((prev) => prev.map((r) => (r.id === id ? { ...r, published } : r)));
      toast({ title: published ? 'Published to Owner Listings' : 'Unpublished', description: published ? 'This property is now live on the site.' : 'Removed from public Owner Listings.' });
    } catch (err) {
      toast({ title: 'Failed to update', description: err.response?.data?.detail || 'Try again' });
    }
  };

  return (
    <AdminLayout title="Property Submissions">
      <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="text-sm text-gray-600"><span className="font-bold text-gray-900">{total}</span> total submissions</div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-500">Filter:</span>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Intents</SelectItem>
              <SelectItem value="sell">Sell</SelectItem>
              <SelectItem value="rent">Rent Out</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#9a1b2f]" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center text-sm text-gray-500">
          <HomeIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          No property submissions yet.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((s) => {
            const intent = INTENT_META[s.intent] || INTENT_META.sell;
            const status = STATUS_META[s.status] || STATUS_META.new;
            const CatIcon = s.listing_category === 'commercial' ? Building2 : HomeIcon;
            return (
              <div key={s.id} className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-xl bg-[#fef2f4] flex items-center justify-center">
                      <User className="w-5 h-5 text-[#9a1b2f]" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-gray-900 truncate">{s.name}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(s.created_at)}</div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${intent.color}`}>{intent.label}</span>
                    <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${status.color}`}>{status.label}</span>
                  </div>
                </div>

                <div className="mt-4 space-y-1.5 text-sm text-gray-700">
                  <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-gray-400" /> <a href={`tel:${s.phone}`} className="hover:text-[#9a1b2f]">{s.phone}</a></div>
                  {s.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-gray-400" /> <a href={`mailto:${s.email}`} className="hover:text-[#9a1b2f]">{s.email}</a></div>}
                  <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-gray-400" /> {[s.location, s.city].filter(Boolean).join(', ') || s.city}</div>
                </div>

                <div className="mt-4 p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-1.5">
                    <CatIcon className="w-3.5 h-3.5" /> {s.listing_category === 'commercial' ? 'Commercial' : 'Residential'}
                    <span className="w-1 h-1 rounded-full bg-gray-300" />
                    <Tag className="w-3.5 h-3.5" /> <span className="capitalize">{s.property_type}</span>
                  </div>
                  <div className="font-semibold text-gray-900 text-sm">{s.property_name || '—'}</div>
                  <div className="grid grid-cols-3 gap-2 mt-2 text-xs text-gray-600">
                    <div><div className="text-[10px] uppercase text-gray-400">Config</div><div className="font-semibold text-gray-900">{s.bhk || '—'}</div></div>
                    <div><div className="text-[10px] uppercase text-gray-400">Area</div><div className="font-semibold text-gray-900">{s.sqft ? `${s.sqft} sqft` : '—'}</div></div>
                    <div><div className="text-[10px] uppercase text-gray-400">Expected</div><div className="font-semibold text-[#9a1b2f]">{s.expected_price || '—'}</div></div>
                  </div>
                </div>

                {s.message && <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-2 mt-2">“{s.message}”</div>}

                {Array.isArray(s.images) && s.images.length > 0 && (
                  <div className="mt-3">
                    <div className="text-[10px] uppercase text-gray-400 tracking-wider font-semibold flex items-center gap-1 mb-1.5">
                      <Camera className="w-3 h-3" /> {s.images.length} photo{s.images.length > 1 ? 's' : ''}
                    </div>
                    <div className="grid grid-cols-4 gap-1.5">
                      {s.images.slice(0, 4).map((img, i) => (
                        <a key={i} href={img} target="_blank" rel="noopener noreferrer" className="aspect-square rounded-lg overflow-hidden border border-gray-100 bg-gray-50">
                          <img src={img} alt={`P${i + 1}`} className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-4 pt-3 border-t border-gray-100 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {s.published ? <Eye className="w-3.5 h-3.5 text-emerald-500" /> : <EyeOff className="w-3.5 h-3.5 text-gray-400" />}
                      <span className="text-xs font-semibold text-gray-700">{s.published ? 'Published to Owner Listings' : 'Not published'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {s.published && (
                        <a href={`/owner-listings/${s.id}`} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#9a1b2f] hover:underline inline-flex items-center gap-1">
                          View <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      <Switch checked={!!s.published} onCheckedChange={(v) => togglePublish(s.id, v)} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Status:</span>
                    <Select value={s.status} onValueChange={(v) => changeStatus(s.id, v)}>
                      <SelectTrigger className="h-8 text-xs w-36"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminPropertySubmissionsPage;
